import { useState, useEffect, useCallback } from 'react';
import { usersAPI, ordersAPI, qcAPI, alterationsAPI, settingsAPI, clientsAPI, notificationsAPI, appointmentsAPI, styleAdvisorAPI, tryonAPI, engagementAPI, internationalAPI } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Plus, MoreHorizontal, Pencil, UserX, UserCheck, Search, Users,
  Settings, Shirt, IndianRupee, ClipboardCheck, Clock, Calculator, Building,
  Save, Loader2, Trash2, Archive, RotateCcw, Bell, MessageSquare, Eye,
  CalendarDays, Copy, ExternalLink, Zap, Scissors, Sparkles, Filter, X,
  Gift, Camera, Key, Heart, ShoppingBag, Video, Globe,
} from 'lucide-react';
import ConfiguratorIllustrationsSection from '@/components/ConfiguratorIllustrationsSection';

const ROLE_BADGE_STYLES = {
  Admin: 'bg-[rgba(27,42,74,0.10)] text-[#1B2A4A] border border-[rgba(27,42,74,0.18)]',
  Sales: 'bg-[rgba(197,165,90,0.16)] text-[#1B2A4A] border border-[rgba(197,165,90,0.30)]',
  Production: 'bg-[rgba(30,122,95,0.12)] text-[#1E7A5F] border border-[rgba(30,122,95,0.22)]',
  Karigar: 'bg-[rgba(180,71,8,0.10)] text-[#B54708] border border-[rgba(180,71,8,0.20)]',
};
const ROLES = ['Admin', 'Sales', 'Production', 'Karigar'];
const GARMENT_TYPES = ['jacket', 'trousers', 'shirt', 'sherwani', 'bandhgala', 'nehru_vest', 'kurta', 'pyjama', 'waistcoat'];
const GARMENT_LABELS = {
  jacket: 'Jacket', trousers: 'Trousers', shirt: 'Shirt', sherwani: 'Sherwani',
  bandhgala: 'Bandhgala', nehru_vest: 'Nehru Vest', kurta: 'Kurta', pyjama: 'Pyjama', waistcoat: 'Waistcoat',
};

const TABS = [
  { key: 'team', label: 'Team Members', icon: Users },
  { key: 'archived_clients', label: 'Archived Clients', icon: Archive },
  { key: 'tax_config', label: 'Tax / GST', icon: Calculator },
  { key: 'notification_templates', label: 'Notifications', icon: Bell },
  { key: 'engagements', label: 'Engagements', icon: Heart },
  { key: 'currency', label: 'Currency', icon: IndianRupee },
  { key: 'consultation', label: 'Consultation', icon: Camera },
  { key: 'appointments', label: 'Appointments', icon: CalendarDays },
  { key: 'referral_tiers', label: 'Referral Tiers', icon: Gift },
  { key: 'tryon_config', label: 'Try-On AI', icon: Camera },
  { key: 'style_rules', label: 'Style Rules', icon: Sparkles },
  { key: 'garment_defaults', label: 'Garment Defaults', icon: Shirt },
  { key: 'making_charges', label: 'Making Charges', icon: IndianRupee },
  { key: 'express', label: 'Express / Rush', icon: Zap },
  { key: 'qc_templates', label: 'QC Templates', icon: ClipboardCheck },
  { key: 'alteration_matrix', label: 'Alteration Matrix', icon: Clock },
  { key: 'alteration_charges', label: 'Alteration Charges', icon: Scissors },
  { key: 'consumption_rules', label: 'Consumption Rules', icon: Calculator },
  { key: 'overhead', label: 'Overhead', icon: Building },
  { key: 'configurator_illustrations', label: 'Illustrations', icon: Eye },
];

export default function SettingsPage() {
  const { user: currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('team');

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: 'var(--brand-navy)' }}>Settings</h1>
        <p className="text-sm mt-1" style={{ color: 'var(--ink-muted)' }}>Manage team, defaults, and configuration</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 border-b overflow-x-auto" style={{ borderColor: 'rgba(27,42,74,0.08)' }} data-testid="settings-tabs">
        {TABS.map(tab => {
          const Icon = tab.icon;
          return (
            <button key={tab.key} onClick={() => setActiveTab(tab.key)}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold whitespace-nowrap transition-colors"
              style={{
                color: activeTab === tab.key ? 'var(--brand-navy)' : 'var(--ink-muted)',
                borderBottom: activeTab === tab.key ? '2px solid var(--brand-navy)' : '2px solid transparent',
              }}
              data-testid={`settings-tab-${tab.key}`}>
              <Icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'team' && <TeamMembersSection currentUser={currentUser} />}
      {activeTab === 'archived_clients' && <ArchivedClientsSection />}
      {activeTab === 'notification_templates' && <NotificationTemplatesSection />}
      {activeTab === 'engagements' && <EngagementSettingsSection />}
      {activeTab === 'currency' && <CurrencySettingsSection />}
      {activeTab === 'tax_config' && <TaxConfigSection />}
      {activeTab === 'consultation' && <ConsultationSettingsSection />}
      {activeTab === 'appointments' && <AppointmentSettingsSection />}
      {activeTab === 'referral_tiers' && <ReferralTiersSection />}
      {activeTab === 'tryon_config' && <TryOnConfigSection />}
      {activeTab === 'style_rules' && <StyleRulesSection />}
      {activeTab === 'garment_defaults' && <GarmentDefaultsSection />}
      {activeTab === 'making_charges' && <MakingChargesSection />}
      {activeTab === 'express' && <ExpressSurchargeSection />}
      {activeTab === 'qc_templates' && <QCTemplatesSection />}
      {activeTab === 'alteration_matrix' && <AlterationMatrixSection />}
      {activeTab === 'alteration_charges' && <AlterationChargesSection />}
      {activeTab === 'consumption_rules' && <ConsumptionRulesSection />}
      {activeTab === 'overhead' && <OverheadSection />}
      {activeTab === 'configurator_illustrations' && <ConfiguratorIllustrationsSection />}
    </div>
  );
}

/* ============================================================
   Team Members Section
   ============================================================ */
function TeamMembersSection({ currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'Sales', is_active: true });
  const [formErrors, setFormErrors] = useState({});

  const fetchUsers = useCallback(async () => {
    try { const r = await usersAPI.list(); setUsers(r.data); } catch { toast.error('Failed to load users'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const openCreateSheet = () => { setEditingUser(null); setFormData({ name: '', email: '', password: '', role: 'Sales', is_active: true }); setFormErrors({}); setSheetOpen(true); };
  const openEditSheet = (u) => { setEditingUser(u); setFormData({ name: u.name, email: u.email, password: '', role: u.role, is_active: u.is_active }); setFormErrors({}); setSheetOpen(true); };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Required';
    if (!formData.email.trim()) errors.email = 'Required';
    if (!editingUser && !formData.password) errors.password = 'Required';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      if (editingUser) {
        const d = { name: formData.name, email: formData.email, role: formData.role, is_active: formData.is_active };
        if (formData.password) d.password = formData.password;
        await usersAPI.update(editingUser.id, d);
        toast.success('User updated');
      } else {
        await usersAPI.create(formData);
        toast.success('User created');
      }
      setSheetOpen(false); fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setSaving(false); }
  };

  const toggleStatus = async (u) => {
    try { await usersAPI.update(u.id, { is_active: !u.is_active }); toast.success(`User ${u.is_active ? 'deactivated' : 'activated'}`); fetchUsers(); } catch { toast.error('Failed'); }
  };

  const filtered = users.filter(u => { const q = searchQuery.toLowerCase(); return u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q); });

  return (
    <>
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
          <Input data-testid="settings-user-search" placeholder="Search users..." value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)} className="h-10 pl-9 rounded-lg" />
        </div>
        <Button data-testid="settings-create-user-button" onClick={openCreateSheet}
          className="h-10 rounded-lg px-4 text-sm" style={{ backgroundColor: 'var(--brand-navy)' }}>
          <Plus className="w-4 h-4 mr-1" /> Create User
        </Button>
      </div>

      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardContent className="p-0">
          {loading ? <div className="p-6"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full mt-2" /></div> : filtered.length === 0 ? (
            <div className="py-12 text-center"><Users className="w-8 h-8 mx-auto mb-2 opacity-20" /><p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No users found</p></div>
          ) : (
            <Table data-testid="settings-users-table">
              <TableHeader>
                <TableRow className="border-t border-border/60">
                  <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Name</TableHead>
                  <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Email</TableHead>
                  <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Role</TableHead>
                  <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Status</TableHead>
                  <TableHead className="text-right text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map(u => (
                  <TableRow key={u.id} className="hover:bg-[color:var(--surface-2)]">
                    <TableCell className="font-medium" style={{ color: 'var(--navy-ink)' }}>{u.name}</TableCell>
                    <TableCell className="text-sm" style={{ color: 'var(--ink-muted)' }}>{u.email}</TableCell>
                    <TableCell><Badge variant="outline" className={`text-xs px-2 py-0.5 rounded-md ${ROLE_BADGE_STYLES[u.role] || ''}`}>{u.role}</Badge></TableCell>
                    <TableCell><span className={`text-xs font-medium ${u.is_active ? 'text-[#1E7A5F]' : 'text-[#B42318]'}`}>{u.is_active ? 'Active' : 'Inactive'}</span></TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="sm" className="h-8 w-8 p-0"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-44">
                          <DropdownMenuItem onClick={() => openEditSheet(u)} className="gap-2"><Pencil className="w-3.5 h-3.5" /> Edit</DropdownMenuItem>
                          {u.id !== currentUser?.id && <DropdownMenuItem onClick={() => toggleStatus(u)} className="gap-2">{u.is_active ? <><UserX className="w-3.5 h-3.5" /> Deactivate</> : <><UserCheck className="w-3.5 h-3.5" /> Activate</>}</DropdownMenuItem>}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent data-testid="settings-user-edit-sheet" className="sm:max-w-[440px] bg-white">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>{editingUser ? 'Edit User' : 'Create User'}</SheetTitle>
            <SheetDescription style={{ color: 'var(--ink-muted)' }}>{editingUser ? 'Update user details' : 'Add team member'}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-6">
            <div><Label className="text-sm">Name</Label><Input data-testid="user-form-name" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="h-10 mt-1" />{formErrors.name && <p className="text-xs text-red-600 mt-1">{formErrors.name}</p>}</div>
            <div><Label className="text-sm">Email</Label><Input data-testid="user-form-email" type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} className="h-10 mt-1" />{formErrors.email && <p className="text-xs text-red-600 mt-1">{formErrors.email}</p>}</div>
            <div><Label className="text-sm">Password{editingUser ? ' (blank = keep)' : ''}</Label><Input data-testid="user-form-password" type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="h-10 mt-1" />{formErrors.password && <p className="text-xs text-red-600 mt-1">{formErrors.password}</p>}</div>
            <div><Label className="text-sm">Role</Label>
              <Select value={formData.role} onValueChange={v => setFormData({...formData, role: v})}>
                <SelectTrigger className="h-10 mt-1" data-testid="settings-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {editingUser && <div className="flex items-center justify-between"><Label>Active</Label><Switch checked={formData.is_active} onCheckedChange={c => setFormData({...formData, is_active: c})} /></div>}
          </div>
          <SheetFooter className="mt-8 flex gap-3">
            <Button variant="outline" onClick={() => setSheetOpen(false)} className="flex-1 h-10 rounded-lg">Cancel</Button>
            <Button data-testid="user-form-save" onClick={handleSave} disabled={saving} className="flex-1 h-10 rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}>{saving ? 'Saving...' : editingUser ? 'Update' : 'Create'}</Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </>
  );
}

/* ============================================================
   Archived Clients Section
   ============================================================ */
function ArchivedClientsSection() {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(null);

  const fetch = useCallback(async () => {
    try { const r = await clientsAPI.listArchived(); setClients(r.data || []); } catch { toast.error('Failed to load'); } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetch(); }, [fetch]);

  const handleRestore = async (id) => {
    setRestoring(id);
    try { await clientsAPI.restore(id); toast.success('Client restored'); fetch(); } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); } finally { setRestoring(null); }
  };

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="archived-clients-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Archived Clients</CardTitle>
      </CardHeader>
      <CardContent>
        {clients.length === 0 ? (
          <p className="text-xs py-6 text-center" style={{ color: 'var(--ink-muted)' }}>No archived clients.</p>
        ) : (
          <Table data-testid="archived-clients-table">
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Name</TableHead>
                <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Phone</TableHead>
                <TableHead className="text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Archived On</TableHead>
                <TableHead className="text-right text-xs font-medium uppercase" style={{ color: 'var(--ink-muted)' }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map(c => (
                <TableRow key={c.id}>
                  <TableCell className="font-medium" style={{ color: 'var(--navy-ink)' }}>{c.first_name} {c.last_name}</TableCell>
                  <TableCell className="text-sm" style={{ color: 'var(--ink-muted)' }}>{c.phone}</TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--ink-muted)' }}>{c.deleted_at ? new Date(c.deleted_at).toLocaleDateString() : '—'}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                      disabled={restoring === c.id}
                      onClick={() => handleRestore(c.id)}
                      data-testid={`restore-client-${c.id}`}>
                      <RotateCcw className="w-3 h-3" /> {restoring === c.id ? 'Restoring...' : 'Restore'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}



/* ============================================================
   Notification Templates Section
   ============================================================ */
const TRIGGER_LABEL_MAP = {
  order_confirmed: 'Order Confirmed', fabric_ordered: 'Fabric Ordered',
  fabric_received: 'Fabric Received', production_started: 'Production Started',
  ready_for_trial: 'Ready for Trial', alterations_complete: 'Alterations Complete',
  ready_for_delivery: 'Ready for Delivery', delivered: 'Delivered',
  payment_received: 'Payment Received', appointment_reminder_24h: 'Reminder (24h)',
  appointment_reminder_2h: 'Reminder (2h)', delay_alert: 'Delay Alert',
  birthday_greeting: 'Birthday', feedback_request: 'Feedback Request',
};

function NotificationTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [editText, setEditText] = useState('');
  const [editChannel, setEditChannel] = useState('whatsapp');
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  useEffect(() => {
    notificationsAPI.listTemplates().then(res => {
      setTemplates(res.data?.templates || []);
      setPlaceholders(res.data?.available_placeholders || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleToggle = async (t) => {
    try {
      const res = await notificationsAPI.updateTemplate(t.id, { is_active: !t.is_active });
      setTemplates(prev => prev.map(x => x.id === t.id ? res.data : x));
      toast.success(res.data.is_active ? 'Template activated' : 'Template deactivated');
    } catch { toast.error('Failed to update'); }
  };

  const openEdit = (t) => {
    setEditing(t);
    setEditText(t.template_text);
    setEditChannel(t.channel || 'whatsapp');
  };

  const handleSave = async () => {
    if (!editing) return;
    setSaving(true);
    try {
      const res = await notificationsAPI.updateTemplate(editing.id, { template_text: editText, channel: editChannel });
      setTemplates(prev => prev.map(x => x.id === editing.id ? res.data : x));
      setEditing(null);
      toast.success('Template saved');
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const handlePreview = async (t) => {
    setPreviewLoading(true);
    setPreview(null);
    try {
      const res = await notificationsAPI.render({
        trigger_event: t.trigger_event,
        extra_data: {
          client_name: 'Kabir Mehra', order_number: 'HS-2603-0001',
          garment_summary: '2-piece Suit (Jacket + Trousers)',
          estimated_date: '15 Apr 2026', lead_time: '7-10',
          amount: '15,000', balance: '35,000', currency: 'INR ',
          balance_text: 'Outstanding balance: INR 35,000',
        },
      });
      setPreview(res.data);
    } catch { toast.error('Preview failed'); }
    setPreviewLoading(false);
  };

  if (loading) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="space-y-4">
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="notification-templates-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>WhatsApp Notification Templates</CardTitle>
              <p className="text-xs mt-0.5" style={{ color: 'var(--ink-muted)' }}>Auto-triggered when order status changes. Toggle to enable/disable.</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          {templates.map(t => (
            <div key={t.id} className="flex items-start gap-3 p-3 rounded-lg border transition-colors"
              style={{ borderColor: t.is_active ? 'rgba(27,42,74,0.12)' : 'rgba(27,42,74,0.06)', opacity: t.is_active ? 1 : 0.6 }}
              data-testid={`template-${t.trigger_event}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-medium"
                    style={{ backgroundColor: t.is_active ? 'rgba(16,185,129,0.08)' : 'transparent', color: t.is_active ? '#059669' : 'var(--ink-muted)' }}>
                    {TRIGGER_LABEL_MAP[t.trigger_event] || t.trigger_event}
                  </Badge>
                  <Badge variant="outline" className="text-[9px] px-1 py-0">
                    <MessageSquare className="w-2.5 h-2.5 mr-0.5" />{t.channel}
                  </Badge>
                </div>
                <p className="text-xs whitespace-pre-wrap leading-relaxed" style={{ color: 'var(--navy-ink)' }}>
                  {t.template_text.length > 150 ? t.template_text.slice(0, 150) + '...' : t.template_text}
                </p>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePreview(t)}
                  data-testid={`preview-${t.trigger_event}`}>
                  <Eye className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
                </Button>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)}
                  data-testid={`edit-${t.trigger_event}`}>
                  <Pencil className="w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
                </Button>
                <Switch checked={t.is_active} onCheckedChange={() => handleToggle(t)}
                  data-testid={`toggle-${t.trigger_event}`} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Edit Template Sheet */}
      <Sheet open={!!editing} onOpenChange={(open) => { if (!open) setEditing(null); }}>
        <SheetContent side="right" className="w-[500px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>Edit Template</SheetTitle>
            <SheetDescription>
              {editing && (TRIGGER_LABEL_MAP[editing.trigger_event] || editing.trigger_event)}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Channel</Label>
              <Select value={editChannel} onValueChange={setEditChannel}>
                <SelectTrigger className="h-8 mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="both">Both</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Message Template</Label>
              <Textarea value={editText} onChange={e => setEditText(e.target.value)}
                className="mt-1 text-xs font-mono h-40" data-testid="template-text-input" />
            </div>
            <div>
              <Label className="text-[10px] font-medium" style={{ color: 'var(--ink-muted)' }}>Available Placeholders</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {placeholders.map(p => (
                  <button key={p} onClick={() => setEditText(prev => prev + p)}
                    className="text-[9px] px-1.5 py-0.5 rounded border font-mono hover:bg-gray-50 transition-colors"
                    style={{ borderColor: 'rgba(27,42,74,0.12)', color: 'var(--brand-navy)' }}>
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="save-template">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Preview Dialog */}
      {preview && (
        <Sheet open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
          <SheetContent side="right" className="w-[420px]">
            <SheetHeader>
              <SheetTitle style={{ color: 'var(--brand-navy)' }}>Message Preview</SheetTitle>
              <SheetDescription>Sample rendering with test data</SheetDescription>
            </SheetHeader>
            <div className="mt-4 space-y-3">
              <div className="p-4 rounded-xl" style={{ backgroundColor: '#DCF8C6', border: '1px solid #C5E1A5' }}>
                <p className="text-sm whitespace-pre-wrap leading-relaxed" style={{ color: '#1B2A4A' }}>
                  {preview.rendered_text}
                </p>
              </div>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                This is how the message will appear with sample data. Actual values will be filled from client/order data.
              </p>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
}



/* ============================================================
   Garment Defaults Section
   ============================================================ */
function GarmentDefaultsSection() {
  const [defaults, setDefaults] = useState(null);
  const [specs, setSpecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedGarment, setSelectedGarment] = useState('jacket');

  useEffect(() => {
    ordersAPI.customisationSpec().then(res => {
      setSpecs(res.data.specs);
      setDefaults(res.data.house_defaults);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await ordersAPI.updateHouseDefaults(defaults);
      toast.success('House defaults saved');
    } catch (err) { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-48 w-full" />;
  if (!defaults || !specs) return <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>No customisation data</p>;

  // specs[garment] is an array of { key, label, group, options }
  const garmentSpecsArray = specs[selectedGarment] || [];
  const garmentDefaults = defaults[selectedGarment] || {};

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="garment-defaults-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>House Defaults</CardTitle>
          <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
            onClick={handleSave} disabled={saving} data-testid="save-garment-defaults">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex gap-1 mb-4 overflow-x-auto">
          {GARMENT_TYPES.map(g => (
            <button key={g} onClick={() => setSelectedGarment(g)}
              className="px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap"
              style={{
                backgroundColor: selectedGarment === g ? 'var(--brand-navy)' : 'rgba(27,42,74,0.04)',
                color: selectedGarment === g ? '#fff' : 'var(--navy-ink)',
              }}>{GARMENT_LABELS[g]}</button>
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {garmentSpecsArray.map(spec => (
            <div key={spec.key}>
              <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{spec.label || spec.key.replace(/_/g, ' ')}</Label>
              {spec.options ? (
                <Select value={garmentDefaults[spec.key] || ''} onValueChange={v => {
                  setDefaults(prev => ({ ...prev, [selectedGarment]: { ...prev[selectedGarment], [spec.key]: v } }));
                }}>
                  <SelectTrigger className="h-8 mt-0.5 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    {spec.options.map(o => {
                      const val = typeof o === 'object' ? o.value : o;
                      const lbl = typeof o === 'object' ? o.label : o;
                      return <SelectItem key={val} value={val}>{lbl}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={garmentDefaults[spec.key] || ''} className="h-8 mt-0.5 text-xs"
                  onChange={e => setDefaults(prev => ({ ...prev, [selectedGarment]: { ...prev[selectedGarment], [spec.key]: e.target.value } }))} />
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================
   Making Charges Section
   ============================================================ */
function MakingChargesSection() {
  const [charges, setCharges] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI.getMakingCharges().then(res => { setCharges(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await settingsAPI.updateMakingCharges(charges); toast.success('Making charges saved'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="making-charges-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Making Charges Defaults (INR)</CardTitle>
          <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
            onClick={handleSave} disabled={saving} data-testid="save-making-charges">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {GARMENT_TYPES.map(g => (
            <div key={g}>
              <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{GARMENT_LABELS[g]}</Label>
              <div className="flex items-center gap-1 mt-0.5">
                <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>₹</span>
                <Input type="number" value={charges[g] || ''} className="h-8 text-xs"
                  onChange={e => setCharges(prev => ({ ...prev, [g]: parseInt(e.target.value) || 0 }))} />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================
   Express Surcharge Section
   ============================================================ */
function ExpressSurchargeSection() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI.getExpressSettings().then(res => { setSettings(res.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await settingsAPI.updateExpressSettings(settings); toast.success('Express settings saved'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-32 w-full" />;
  if (!settings) return <p className="text-sm" style={{ color: 'var(--ink-muted)' }}>Failed to load</p>;

  return (
    <div className="space-y-4" data-testid="express-surcharge-section">
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Zap className="w-4 h-4" style={{ color: '#D97706' }} /> Express Threshold
            </CardTitle>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-express-settings">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
            Orders with a required-by date within this many days from today are flagged as Express.
          </p>
          <div className="flex items-center gap-2 max-w-xs">
            <Input type="number" value={settings.express_threshold_days || 7} className="h-9 text-sm w-24"
              onChange={e => setSettings(prev => ({ ...prev, express_threshold_days: parseInt(e.target.value) || 7 }))}
              data-testid="express-threshold-input" />
            <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>days</span>
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Express Surcharge per Garment (%)</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
            Percentage surcharge applied on making charges when an order is marked Express.
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {GARMENT_TYPES.map(g => (
              <div key={g}>
                <Label className="text-[10px] uppercase tracking-wide" style={{ color: 'var(--ink-muted)' }}>{GARMENT_LABELS[g]}</Label>
                <div className="flex items-center gap-1 mt-0.5">
                  <Input type="number" value={settings.surcharges?.[g] ?? 25} className="h-8 text-xs w-20"
                    onChange={e => setSettings(prev => ({
                      ...prev, surcharges: { ...(prev.surcharges || {}), [g]: parseInt(e.target.value) || 0 }
                    }))}
                    data-testid={`express-surcharge-${g}`} />
                  <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>%</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================
   Alteration Customer Charges Section
   ============================================================ */
function AlterationChargesSection() {
  const [charges, setCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    import('@/lib/api').then(({ standaloneAlterationsAPI }) => {
      standaloneAlterationsAPI.charges().then(r => { setCharges(r.data || []); setLoading(false); }).catch(() => setLoading(false));
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { standaloneAlterationsAPI } = await import('@/lib/api');
      await standaloneAlterationsAPI.updateCharges(charges);
      toast.success('Alteration charges saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const addEntry = () => {
    setCharges(prev => [...prev, {
      id: Date.now().toString(),
      garment_type: 'other', alteration_type: 'other', garment_area: 'other',
      customer_charge: 500, estimated_hours: 1, is_active: true,
    }]);
  };

  const updateEntry = (idx, field, value) => {
    setCharges(prev => prev.map((c, i) => i === idx ? { ...c, [field]: value } : c));
  };

  const removeEntry = (idx) => setCharges(prev => prev.filter((_, i) => i !== idx));

  if (loading) return <Skeleton className="h-48 w-full" />;

  const ALT_TYPES = { shorten: 'Shorten', lengthen: 'Lengthen', take_in: 'Take In', let_out: 'Let Out', reshape: 'Reshape', reposition: 'Reposition', reline: 'Reline', patch: 'Patch', zipper: 'Zipper', button_fix: 'Button Fix', other: 'Other' };
  const AREAS = { chest: 'Chest', waist: 'Waist', hips: 'Hips', shoulders: 'Shoulders', sleeves: 'Sleeves', jacket_length: 'Jacket Len.', trouser_length: 'Trouser Len.', trouser_waist: 'Trouser Waist', collar: 'Collar', cuffs: 'Cuffs', lapels: 'Lapels', lining: 'Lining', seat: 'Seat', inseam: 'Inseam', outseam: 'Outseam', other: 'Other' };

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="alteration-charges-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Scissors className="w-4 h-4" style={{ color: '#7C3AED' }} /> Alteration Customer Charges
          </CardTitle>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="gap-1 text-xs rounded-lg h-8" onClick={addEntry}>
              <Plus className="w-3 h-3" /> Add
            </Button>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-alteration-charges">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
          Customer-facing charges for standalone alteration jobs. These auto-fill when creating new alteration jobs.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b" style={{ color: 'var(--ink-muted)' }}>
                <th className="text-left py-1.5 font-semibold">Garment</th>
                <th className="text-left py-1.5 font-semibold">Type</th>
                <th className="text-left py-1.5 font-semibold">Area</th>
                <th className="text-right py-1.5 font-semibold">Charge (₹)</th>
                <th className="text-right py-1.5 font-semibold">Est. Hours</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {charges.map((c, idx) => (
                <tr key={c.id || idx} className="border-b last:border-0" style={{ borderColor: 'rgba(27,42,74,0.06)' }}>
                  <td className="py-1">
                    <select value={c.garment_type} onChange={e => updateEntry(idx, 'garment_type', e.target.value)}
                      className="text-xs h-7 border rounded px-1 bg-white" data-testid={`charge-garment-${idx}`}>
                      {Object.entries(GARMENT_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="py-1">
                    <select value={c.alteration_type} onChange={e => updateEntry(idx, 'alteration_type', e.target.value)}
                      className="text-xs h-7 border rounded px-1 bg-white" data-testid={`charge-alttype-${idx}`}>
                      {Object.entries(ALT_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="py-1">
                    <select value={c.garment_area} onChange={e => updateEntry(idx, 'garment_area', e.target.value)}
                      className="text-xs h-7 border rounded px-1 bg-white" data-testid={`charge-area-${idx}`}>
                      {Object.entries(AREAS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                    </select>
                  </td>
                  <td className="py-1">
                    <Input type="number" value={c.customer_charge} className="h-7 text-xs w-20 ml-auto"
                      onChange={e => updateEntry(idx, 'customer_charge', parseFloat(e.target.value) || 0)}
                      data-testid={`charge-amount-${idx}`} />
                  </td>
                  <td className="py-1">
                    <Input type="number" step="0.25" value={c.estimated_hours} className="h-7 text-xs w-16 ml-auto"
                      onChange={e => updateEntry(idx, 'estimated_hours', parseFloat(e.target.value) || 0)}
                      data-testid={`charge-hours-${idx}`} />
                  </td>
                  <td className="py-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeEntry(idx)}>
                      <Trash2 className="w-3 h-3 text-red-500" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================
   QC Templates Section
   ============================================================ */
function QCTemplatesSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [editItems, setEditItems] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    qcAPI.listTemplates().then(res => { setTemplates(res.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!editingTemplate) return;
    setSaving(true);
    try {
      const items = editItems.split('\n').filter(l => l.trim()).map(l => l.trim());
      await qcAPI.updateTemplate(editingTemplate.id, { checklist_items: items });
      toast.success('Template saved');
      setEditingTemplate(null);
      qcAPI.listTemplates().then(res => setTemplates(res.data || []));
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="qc-templates-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>QC Checklist Templates</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {templates.length === 0 ? (
          <p className="text-xs py-4 text-center" style={{ color: 'var(--ink-muted)' }}>No QC templates. They are auto-seeded on first QC inspection.</p>
        ) : (
          templates.map(t => (
            <div key={t.id} className="rounded-lg border p-3" style={{ borderColor: 'rgba(27,42,74,0.08)' }}
              data-testid={`qc-template-${t.id}`}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-[10px] px-1.5 capitalize">{t.garment_type}</Badge>
                  <Badge variant="outline" className="text-[10px] px-1.5">{t.qc_type?.replace('_', ' ')}</Badge>
                  <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{(t.checklist_items || []).length} checks</span>
                </div>
                <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
                  onClick={() => { setEditingTemplate(t); setEditItems((t.checklist_items || []).join('\n')); }}>
                  <Pencil className="w-3 h-3" /> Edit
                </Button>
              </div>
              <p className="text-[10px] truncate" style={{ color: 'var(--ink-muted)' }}>
                {(t.checklist_items || []).slice(0, 3).join(' · ')}
                {(t.checklist_items || []).length > 3 && ` +${(t.checklist_items || []).length - 3} more`}
              </p>
            </div>
          ))
        )}

        {editingTemplate && (
          <div className="mt-3 p-3 rounded-lg border" style={{ borderColor: 'var(--brand-gold)', backgroundColor: 'rgba(197,165,90,0.04)' }}>
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--brand-navy)' }}>
              Editing: {editingTemplate.garment_type} / {editingTemplate.qc_type}
            </p>
            <Textarea value={editItems} onChange={e => setEditItems(e.target.value)} rows={8}
              className="text-xs font-mono" placeholder="One check per line..." />
            <div className="flex gap-2 mt-2">
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setEditingTemplate(null)}>Cancel</Button>
              <Button size="sm" className="text-xs gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
                onClick={handleSave} disabled={saving}>
                {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ============================================================
   Alteration Time Matrix Section
   ============================================================ */
function AlterationMatrixSection() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    alterationsAPI.timeMatrix().then(res => { setEntries(res.data || []); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await alterationsAPI.updateTimeMatrix(entries); toast.success('Time matrix saved'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const addEntry = () => {
    setEntries(prev => [...prev, { alteration_type: 'other', garment_area: 'other', garment_type: 'any', estimated_hours: 1.0 }]);
  };

  const removeEntry = (i) => setEntries(prev => prev.filter((_, idx) => idx !== i));

  const updateEntry = (i, field, value) => {
    setEntries(prev => { const u = [...prev]; u[i] = { ...u[i], [field]: value }; return u; });
  };

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="alteration-matrix-section">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Alteration Time Matrix</CardTitle>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="gap-1 text-xs h-7" onClick={addEntry}>
              <Plus className="w-3 h-3" /> Add Row
            </Button>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-alteration-matrix">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--ink-muted)' }}>Type</th>
                <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--ink-muted)' }}>Area</th>
                <th className="text-left py-2 px-2 font-medium" style={{ color: 'var(--ink-muted)' }}>Garment</th>
                <th className="text-right py-2 px-2 font-medium" style={{ color: 'var(--ink-muted)' }}>Hours</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {entries.map((e, i) => (
                <tr key={i} className="border-b" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                  <td className="py-1.5 px-2">
                    <Input value={e.alteration_type || ''} className="h-7 text-xs"
                      onChange={ev => updateEntry(i, 'alteration_type', ev.target.value)} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Input value={e.garment_area || ''} className="h-7 text-xs"
                      onChange={ev => updateEntry(i, 'garment_area', ev.target.value)} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Input value={e.garment_type || ''} className="h-7 text-xs"
                      onChange={ev => updateEntry(i, 'garment_type', ev.target.value)} />
                  </td>
                  <td className="py-1.5 px-2">
                    <Input type="number" step="0.25" value={e.estimated_hours ?? ''} className="h-7 text-xs text-right"
                      onChange={ev => updateEntry(i, 'estimated_hours', parseFloat(ev.target.value) || 0)} />
                  </td>
                  <td className="py-1.5 px-1">
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeEntry(i)}>
                      <Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/* ============================================================
   Consumption Rules Section
   ============================================================ */
function ConsumptionRulesSection() {
  const [rules, setRules] = useState([]);
  const [modifiers, setModifiers] = useState({ pattern_matching_extra_pct: 15, default_wastage_pct: 10, reference_fabric_width_cm: 150, pattern_types: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedGarment, setExpandedGarment] = useState(null);

  useEffect(() => {
    Promise.all([
      ordersAPI.consumptionRules().then(res => res.data || []),
      settingsAPI.get('consumption_modifiers').then(res => res.data?.value || modifiers).catch(() => modifiers),
    ]).then(([r, m]) => {
      setRules(r);
      if (m && typeof m === 'object') setModifiers(prev => ({ ...prev, ...m }));
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const grouped = GARMENT_TYPES.reduce((acc, g) => {
    acc[g] = rules.filter(r => r.garment_type === g);
    return acc;
  }, {});

  const handleSave = async () => {
    setSaving(true);
    try {
      await ordersAPI.updateConsumptionRules(rules);
      await settingsAPI.update('consumption_modifiers', modifiers);
      toast.success('Consumption rules saved');
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  const addRule = (garmentType) => {
    setRules(prev => [...prev, { garment_type: garmentType, component: '', count: 1, length_formula: '', width_formula: '', length_default: 0, width_default: 0 }]);
    setExpandedGarment(garmentType);
  };

  const updateRule = (index, field, value) => {
    setRules(prev => { const u = [...prev]; u[index] = { ...u[index], [field]: value }; return u; });
  };

  const deleteRule = (index) => {
    setRules(prev => prev.filter((_, i) => i !== index));
  };

  if (loading) return <Skeleton className="h-32 w-full" />;

  return (
    <div className="space-y-4">
      {/* Modifiers Card */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Global Modifiers</CardTitle>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-consumption-rules">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save All
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Pattern Matching Extra (%)</Label>
              <Input type="number" value={modifiers.pattern_matching_extra_pct} className="h-8 mt-1 text-xs"
                onChange={e => setModifiers(prev => ({ ...prev, pattern_matching_extra_pct: parseInt(e.target.value) || 0 }))} />
              <p className="text-[10px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Applies to: stripe, check, plaid, herringbone</p>
            </div>
            <div>
              <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Default Wastage (%)</Label>
              <Input type="number" value={modifiers.default_wastage_pct} className="h-8 mt-1 text-xs"
                onChange={e => setModifiers(prev => ({ ...prev, default_wastage_pct: parseInt(e.target.value) || 0 }))} />
            </div>
            <div>
              <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Reference Fabric Width (cm)</Label>
              <Input type="number" value={modifiers.reference_fabric_width_cm} className="h-8 mt-1 text-xs"
                onChange={e => setModifiers(prev => ({ ...prev, reference_fabric_width_cm: parseInt(e.target.value) || 150 }))} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Per-garment rules */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="consumption-rules-section">
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Component Rules by Garment</CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          {GARMENT_TYPES.map(g => {
            const components = grouped[g] || [];
            const isExpanded = expandedGarment === g;
            return (
              <div key={g} className="border rounded-lg" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                <button className="w-full flex items-center justify-between px-3 py-2 hover:bg-gray-50 transition-colors"
                  onClick={() => setExpandedGarment(isExpanded ? null : g)} data-testid={`toggle-${g}-rules`}>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>{GARMENT_LABELS[g]}</span>
                    <Badge variant="outline" className="text-[9px] px-1 py-0">{components.length} components</Badge>
                  </div>
                  <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>{isExpanded ? '−' : '+'}</span>
                </button>
                {isExpanded && (
                  <div className="px-3 pb-3">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                          <th className="text-left py-1.5 px-1 font-medium" style={{ color: 'var(--ink-muted)' }}>Component</th>
                          <th className="text-center py-1.5 px-1 font-medium w-12" style={{ color: 'var(--ink-muted)' }}>Qty</th>
                          <th className="text-left py-1.5 px-1 font-medium" style={{ color: 'var(--ink-muted)' }}>Length Formula</th>
                          <th className="text-left py-1.5 px-1 font-medium" style={{ color: 'var(--ink-muted)' }}>Width Formula</th>
                          <th className="w-8"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {rules.map((r, i) => {
                          if (r.garment_type !== g) return null;
                          return (
                            <tr key={i} className="border-b" style={{ borderColor: 'rgba(27,42,74,0.04)' }}>
                              <td className="py-1 px-1"><Input value={r.component || ''} className="h-7 text-xs" placeholder="e.g. Body Front"
                                onChange={e => updateRule(i, 'component', e.target.value)} /></td>
                              <td className="py-1 px-1"><Input type="number" value={r.count ?? 1} className="h-7 text-xs text-center w-12"
                                onChange={e => updateRule(i, 'count', parseInt(e.target.value) || 1)} /></td>
                              <td className="py-1 px-1"><Input value={r.length_formula || ''} className="h-7 text-xs font-mono" placeholder="jacket_length + 10"
                                onChange={e => updateRule(i, 'length_formula', e.target.value)} /></td>
                              <td className="py-1 px-1"><Input value={r.width_formula || ''} className="h-7 text-xs font-mono" placeholder="chest/2 + 10"
                                onChange={e => updateRule(i, 'width_formula', e.target.value)} /></td>
                              <td className="py-1 px-1"><Button variant="ghost" size="sm" className="h-6 w-6 p-0"
                                onClick={() => deleteRule(i)}><Trash2 className="w-3 h-3" style={{ color: '#DC2626' }} /></Button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <Button variant="outline" size="sm" className="gap-1 text-[10px] h-6 mt-2" onClick={() => addRule(g)}>
                      <Plus className="w-3 h-3" /> Add Component
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================
   Overhead Section
   ============================================================ */
function OverheadSection() {
  const [overhead, setOverhead] = useState(1200);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    settingsAPI.get('overhead_per_order').then(res => {
      if (res.data?.value?.amount != null) setOverhead(res.data.value.amount);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try { await settingsAPI.update('overhead_per_order', { amount: overhead }); toast.success('Overhead saved'); } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  if (loading) return <Skeleton className="h-24 w-full" />;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="overhead-section">
      <CardHeader className="pb-2">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Overhead Per Order</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-4">
          <div className="flex-1 max-w-xs">
            <Label className="text-xs" style={{ color: 'var(--ink-muted)' }}>Default overhead allocation per order (INR)</Label>
            <div className="flex items-center gap-1 mt-1">
              <span className="text-sm font-medium" style={{ color: 'var(--ink-muted)' }}>₹</span>
              <Input type="number" value={overhead} className="h-10 text-sm"
                onChange={e => setOverhead(parseInt(e.target.value) || 0)}
                data-testid="overhead-amount-input" />
            </div>
          </div>
          <Button size="sm" className="gap-1 rounded-lg h-10" style={{ backgroundColor: 'var(--brand-navy)' }}
            onClick={handleSave} disabled={saving} data-testid="save-overhead">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
          </Button>
        </div>
        <p className="text-[10px] mt-2" style={{ color: 'var(--ink-muted)' }}>
          This amount is applied to all order cost sheets as the default overhead allocation.
        </p>
      </CardContent>
    </Card>
  );
}


/* ============================================================
   Appointment Settings Section
   ============================================================ */
const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function AppointmentSettingsSection() {
  const [settings, setSettings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [popupEvents, setPopupEvents] = useState([]);
  const [popupForm, setPopupForm] = useState(null);
  const [savingPopup, setSavingPopup] = useState(false);

  const bookingUrl = `${window.location.origin}/book`;

  const loadData = useCallback(async () => {
    try {
      const [settingsRes, eventsRes] = await Promise.all([
        appointmentsAPI.getSettings(),
        appointmentsAPI.popupEvents(),
      ]);
      const s = settingsRes.data || [];
      s.sort((a, b) => a.day_of_week - b.day_of_week);
      setSettings(s);
      setPopupEvents(eventsRes.data || []);
    } catch { toast.error('Failed to load settings'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const updateDay = (dayOfWeek, field, value) => {
    setSettings(prev => prev.map(s => s.day_of_week === dayOfWeek ? { ...s, [field]: value } : s));
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      await appointmentsAPI.updateSettings({ settings });
      toast.success('Appointment settings saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  const handleSavePopup = async () => {
    if (!popupForm?.name || !popupForm?.slug || !popupForm?.start_date || !popupForm?.end_date) {
      toast.error('Fill all required fields'); return;
    }
    setSavingPopup(true);
    try {
      if (popupForm.id) {
        await appointmentsAPI.updatePopupEvent(popupForm.id, popupForm);
      } else {
        await appointmentsAPI.createPopupEvent(popupForm);
      }
      toast.success('Pop-up event saved');
      setPopupForm(null);
      loadData();
    } catch (e) { toast.error('Failed to save event'); }
    setSavingPopup(false);
  };

  const handleDeletePopup = async (id) => {
    try {
      await appointmentsAPI.deletePopupEvent(id);
      toast.success('Deleted');
      loadData();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <Skeleton className="h-64 w-full rounded-xl" />;

  return (
    <div className="space-y-6" data-testid="appointment-settings-section">
      {/* Business Hours */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Business Hours</CardTitle>
            <Button size="sm" className="gap-1 h-8 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSaveSettings} disabled={saving} data-testid="save-appointment-settings">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs">Day</TableHead>
                <TableHead className="text-xs">Open</TableHead>
                <TableHead className="text-xs">From</TableHead>
                <TableHead className="text-xs">To</TableHead>
                <TableHead className="text-xs">Slot (min)</TableHead>
                <TableHead className="text-xs">Max / Slot</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {settings.map(s => (
                <TableRow key={s.day_of_week}>
                  <TableCell className="text-xs font-medium">{DAY_NAMES[s.day_of_week]}</TableCell>
                  <TableCell>
                    <Switch checked={s.is_open} onCheckedChange={v => updateDay(s.day_of_week, 'is_open', v)}
                      data-testid={`day-${s.day_of_week}-open`} />
                  </TableCell>
                  <TableCell>
                    <Input type="time" value={s.open_time || '10:00'} disabled={!s.is_open}
                      onChange={e => updateDay(s.day_of_week, 'open_time', e.target.value)}
                      className="h-7 text-xs w-24" />
                  </TableCell>
                  <TableCell>
                    <Input type="time" value={s.close_time || '19:00'} disabled={!s.is_open}
                      onChange={e => updateDay(s.day_of_week, 'close_time', e.target.value)}
                      className="h-7 text-xs w-24" />
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={s.slot_duration_minutes || 60} disabled={!s.is_open}
                      onChange={e => updateDay(s.day_of_week, 'slot_duration_minutes', parseInt(e.target.value) || 60)}
                      className="h-7 text-xs w-16" min={15} max={240} />
                  </TableCell>
                  <TableCell>
                    <Input type="number" value={s.max_appointments_per_slot || 2} disabled={!s.is_open}
                      onChange={e => updateDay(s.day_of_week, 'max_appointments_per_slot', parseInt(e.target.value) || 2)}
                      className="h-7 text-xs w-14" min={1} max={10} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Public Booking Link */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Public Booking Link</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs mb-2" style={{ color: 'var(--ink-muted)' }}>
            Share this link with clients so they can book appointments online.
          </p>
          <div className="flex items-center gap-2">
            <Input value={bookingUrl} readOnly className="h-8 text-xs flex-1" data-testid="public-booking-url" />
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs"
              onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success('Copied!'); }}
              data-testid="copy-booking-url">
              <Copy className="w-3 h-3" /> Copy
            </Button>
            <Button variant="outline" size="sm" className="h-8 gap-1 text-xs"
              onClick={() => window.open(bookingUrl, '_blank')}>
              <ExternalLink className="w-3 h-3" /> Open
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Pop-Up Events */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Pop-Up Events</CardTitle>
            <Button size="sm" className="gap-1 h-8 text-xs rounded-lg" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={() => setPopupForm({ name: '', venue_name: '', venue_address: '', slug: '', start_date: '', end_date: '', open_time: '10:00', close_time: '19:00', slot_duration_minutes: 60, max_per_slot: 3, is_active: true })}
              data-testid="new-popup-event-btn">
              <Plus className="w-3 h-3" /> New Event
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {popupEvents.length === 0 && !popupForm && (
            <p className="text-xs text-center py-4" style={{ color: 'var(--ink-muted)' }}>No pop-up events configured</p>
          )}
          {popupEvents.map(ev => (
            <div key={ev.id} className="flex items-center justify-between p-3 rounded-lg border mb-2" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>{ev.name}</p>
                <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
                  {ev.venue_name} · {ev.start_date} to {ev.end_date}
                </p>
                <div className="flex items-center gap-2 mt-1">
                  <Input value={`${window.location.origin}/book/${ev.slug}`} readOnly className="h-6 text-[10px] w-64" />
                  <Button variant="ghost" size="sm" className="h-6 px-1"
                    onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/book/${ev.slug}`); toast.success('Copied!'); }}>
                    <Copy className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Badge className="text-[9px]" style={{ backgroundColor: ev.is_active ? 'rgba(16,185,129,0.1)' : 'rgba(239,68,68,0.1)', color: ev.is_active ? '#059669' : '#DC2626' }}>
                  {ev.is_active ? 'Active' : 'Inactive'}
                </Badge>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setPopupForm({ ...ev })}>
                  <Pencil className="w-3 h-3" />
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDeletePopup(ev.id)}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          ))}

          {/* Popup Event Form */}
          {popupForm && (
            <div className="p-3 border rounded-lg mt-2 space-y-3" style={{ borderColor: 'rgba(27,42,74,0.12)', backgroundColor: 'rgba(27,42,74,0.02)' }}>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Event Name *</Label>
                  <Input value={popupForm.name} onChange={e => setPopupForm(p => ({ ...p, name: e.target.value }))}
                    className="h-8 mt-1 text-xs" data-testid="popup-name" />
                </div>
                <div>
                  <Label className="text-xs">URL Slug *</Label>
                  <Input value={popupForm.slug} onChange={e => setPopupForm(p => ({ ...p, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    className="h-8 mt-1 text-xs" placeholder="trunk-show-2026" data-testid="popup-slug" />
                </div>
                <div>
                  <Label className="text-xs">Venue Name *</Label>
                  <Input value={popupForm.venue_name} onChange={e => setPopupForm(p => ({ ...p, venue_name: e.target.value }))}
                    className="h-8 mt-1 text-xs" data-testid="popup-venue" />
                </div>
                <div>
                  <Label className="text-xs">Venue Address *</Label>
                  <Input value={popupForm.venue_address} onChange={e => setPopupForm(p => ({ ...p, venue_address: e.target.value }))}
                    className="h-8 mt-1 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Start Date *</Label>
                  <Input type="date" value={popupForm.start_date} onChange={e => setPopupForm(p => ({ ...p, start_date: e.target.value }))}
                    className="h-8 mt-1 text-xs" data-testid="popup-start-date" />
                </div>
                <div>
                  <Label className="text-xs">End Date *</Label>
                  <Input type="date" value={popupForm.end_date} onChange={e => setPopupForm(p => ({ ...p, end_date: e.target.value }))}
                    className="h-8 mt-1 text-xs" data-testid="popup-end-date" />
                </div>
                <div>
                  <Label className="text-xs">Open Time</Label>
                  <Input type="time" value={popupForm.open_time} onChange={e => setPopupForm(p => ({ ...p, open_time: e.target.value }))}
                    className="h-8 mt-1 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Close Time</Label>
                  <Input type="time" value={popupForm.close_time} onChange={e => setPopupForm(p => ({ ...p, close_time: e.target.value }))}
                    className="h-8 mt-1 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Slot Duration (min)</Label>
                  <Input type="number" value={popupForm.slot_duration_minutes} onChange={e => setPopupForm(p => ({ ...p, slot_duration_minutes: parseInt(e.target.value) || 60 }))}
                    className="h-8 mt-1 text-xs" />
                </div>
                <div>
                  <Label className="text-xs">Max / Slot</Label>
                  <Input type="number" value={popupForm.max_per_slot} onChange={e => setPopupForm(p => ({ ...p, max_per_slot: parseInt(e.target.value) || 3 }))}
                    className="h-8 mt-1 text-xs" />
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Switch checked={popupForm.is_active} onCheckedChange={v => setPopupForm(p => ({ ...p, is_active: v }))} />
                <Label className="text-xs">Active</Label>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" className="text-xs h-8" onClick={() => setPopupForm(null)}>Cancel</Button>
                <Button size="sm" className="text-xs h-8 gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
                  onClick={handleSavePopup} disabled={savingPopup} data-testid="save-popup-event">
                  {savingPopup ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                  {popupForm.id ? 'Update' : 'Create'} Event
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


/* ============================================================
   Style Rules Section (AI Style Advisor Knowledge Base)
   ============================================================ */
const RULE_TYPE_LABELS = {
  colour_season: 'Colour Season',
  body_shape: 'Body Shape',
  face_shape: 'Face Shape',
  occasion: 'Occasion',
};
const RULE_TYPE_COLORS = {
  colour_season: '#7C3AED',
  body_shape: '#059669',
  face_shape: '#2563EB',
  occasion: '#D97706',
};
const STRENGTH_COLORS = {
  strong: '#059669',
  moderate: '#D97706',
  mild: '#6B7280',
};

function StyleRulesSection() {
  const [rules, setRules] = useState([]);
  const [enums, setEnums] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('all');
  const [filterInput, setFilterInput] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [formData, setFormData] = useState({
    rule_type: 'colour_season',
    input_value: '',
    recommendation_type: 'fabric_colour',
    recommended_value: '',
    strength: 'moderate',
    explanation: '',
    is_active: true,
  });

  const fetchData = useCallback(async () => {
    try {
      const params = {};
      if (filterType && filterType !== 'all') params.rule_type = filterType;
      if (filterInput && filterInput !== 'all') params.input_value = filterInput;
      
      const [rulesRes, enumsRes, statsRes] = await Promise.all([
        styleAdvisorAPI.listRules(params),
        styleAdvisorAPI.getEnums(),
        styleAdvisorAPI.getStats(),
      ]);
      setRules(rulesRes.data || []);
      setEnums(enumsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('Failed to load style rules');
    } finally {
      setLoading(false);
    }
  }, [filterType, filterInput]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const res = await styleAdvisorAPI.seedRules();
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Failed to seed rules');
    }
    setSeeding(false);
  };

  const openCreateSheet = () => {
    setEditingRule(null);
    setFormData({
      rule_type: 'colour_season',
      input_value: '',
      recommendation_type: 'fabric_colour',
      recommended_value: '',
      strength: 'moderate',
      explanation: '',
      is_active: true,
    });
    setSheetOpen(true);
  };

  const openEditSheet = (rule) => {
    setEditingRule(rule);
    setFormData({
      rule_type: rule.rule_type,
      input_value: rule.input_value,
      recommendation_type: rule.recommendation_type,
      recommended_value: rule.recommended_value,
      strength: rule.strength,
      explanation: rule.explanation || '',
      is_active: rule.is_active,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.input_value || !formData.recommended_value) {
      toast.error('Input value and recommended value are required');
      return;
    }
    setSaving(true);
    try {
      if (editingRule) {
        await styleAdvisorAPI.updateRule(editingRule.id, formData);
        toast.success('Rule updated');
      } else {
        await styleAdvisorAPI.createRule(formData);
        toast.success('Rule created');
      }
      setSheetOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    }
    setSaving(false);
  };

  const handleDelete = async (rule) => {
    if (!confirm(`Delete this rule? ${rule.input_value} → ${rule.recommended_value}`)) return;
    try {
      await styleAdvisorAPI.deleteRule(rule.id);
      toast.success('Rule deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      await styleAdvisorAPI.updateRule(rule.id, { is_active: !rule.is_active });
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  // Filter rules by search
  const filteredRules = rules.filter(r => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (
      r.input_value?.toLowerCase().includes(q) ||
      r.recommended_value?.toLowerCase().includes(q) ||
      r.explanation?.toLowerCase().includes(q)
    );
  });

  // Get unique input values for current filter type
  const inputValues = filterType && filterType !== 'all'
    ? [...new Set(rules.filter(r => r.rule_type === filterType).map(r => r.input_value))]
    : [];

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-20" /><Skeleton className="h-64" /></div>;
  }

  return (
    <div className="space-y-4" data-testid="style-rules-section">
      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Card className="rounded-lg border bg-white">
            <CardContent className="p-3 text-center">
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>{stats.rules?.active || 0}</p>
              <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>Active Rules</p>
            </CardContent>
          </Card>
          {Object.entries(stats.rules?.by_type || {}).map(([type, count]) => (
            <Card key={type} className="rounded-lg border bg-white">
              <CardContent className="p-3 text-center">
                <p className="text-xl font-bold" style={{ color: RULE_TYPE_COLORS[type] || '#6B7280' }}>{count}</p>
                <p className="text-[10px] uppercase tracking-wide font-semibold" style={{ color: 'var(--ink-muted)' }}>
                  {RULE_TYPE_LABELS[type] || type}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Actions & Filters */}
      <Card className="rounded-xl border bg-white">
        <CardContent className="p-3">
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={openCreateSheet} className="gap-1.5 h-8 text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="add-style-rule-btn">
              <Plus className="w-3.5 h-3.5" /> Add Rule
            </Button>
            {stats?.rules?.total === 0 && (
              <Button onClick={handleSeed} variant="outline" className="gap-1.5 h-8 text-xs" disabled={seeding}
                data-testid="seed-rules-btn">
                {seeding ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                Seed Default Rules
              </Button>
            )}
            <Separator orientation="vertical" className="h-6" />
            <Filter className="w-4 h-4" style={{ color: 'var(--ink-muted)' }} />
            <Select value={filterType} onValueChange={v => { setFilterType(v); setFilterInput(''); }}>
              <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="filter-rule-type">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {Object.entries(RULE_TYPE_LABELS).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {filterType && filterType !== 'all' && inputValues.length > 0 && (
              <Select value={filterInput} onValueChange={setFilterInput}>
                <SelectTrigger className="w-[150px] h-8 text-xs" data-testid="filter-input-value">
                  <SelectValue placeholder="All Values" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Values</SelectItem>
                  {inputValues.map(v => (
                    <SelectItem key={v} value={v}>{v.replace(/_/g, ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <div className="relative flex-1 min-w-[150px]">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--ink-muted)' }} />
              <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search rules..." className="pl-7 h-8 text-xs" data-testid="search-rules" />
            </div>
            {(filterType && filterType !== 'all' || filterInput && filterInput !== 'all' || searchQuery) && (
              <Button variant="ghost" size="sm" onClick={() => { setFilterType('all'); setFilterInput('all'); setSearchQuery(''); }}
                className="h-8 text-xs">Clear</Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card className="rounded-xl border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow style={{ backgroundColor: 'rgba(27,42,74,0.02)' }}>
                <TableHead className="text-[10px] font-semibold uppercase">Type</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase">Input</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase">Rec. Type</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase">Value</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase">Strength</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase">Explanation</TableHead>
                <TableHead className="text-[10px] font-semibold uppercase w-20">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRules.map(rule => (
                <TableRow key={rule.id} className={!rule.is_active ? 'opacity-50' : ''} data-testid={`rule-row-${rule.id}`}>
                  <TableCell>
                    <Badge variant="outline" className="text-[9px] px-1.5"
                      style={{ borderColor: RULE_TYPE_COLORS[rule.rule_type] + '40', color: RULE_TYPE_COLORS[rule.rule_type] }}>
                      {RULE_TYPE_LABELS[rule.rule_type] || rule.rule_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs font-medium" style={{ color: 'var(--navy-ink)' }}>
                    {rule.input_value?.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell className="text-xs" style={{ color: 'var(--ink-muted)' }}>
                    {rule.recommendation_type?.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell className="text-xs font-semibold" style={{ color: 'var(--brand-navy)' }}>
                    {rule.recommended_value?.replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[9px] px-1.5"
                      style={{ borderColor: STRENGTH_COLORS[rule.strength] + '40', color: STRENGTH_COLORS[rule.strength] }}>
                      {rule.strength}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-[10px] max-w-[200px] truncate" style={{ color: 'var(--ink-muted)' }}>
                    {rule.explanation}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Switch checked={rule.is_active} onCheckedChange={() => handleToggleActive(rule)} className="scale-75" />
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => openEditSheet(rule)}>
                        <Pencil className="w-3 h-3" style={{ color: 'var(--ink-muted)' }} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(rule)}>
                        <Trash2 className="w-3 h-3 text-red-400" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredRules.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8" style={{ color: 'var(--ink-muted)' }}>
                    {rules.length === 0 ? 'No rules yet. Click "Seed Default Rules" to load image consulting principles.' : 'No rules match your filters.'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
        {filteredRules.length > 0 && (
          <div className="px-4 py-2 border-t text-[10px]" style={{ borderColor: 'rgba(27,42,74,0.06)', color: 'var(--ink-muted)' }}>
            Showing {filteredRules.length} of {rules.length} rules
          </div>
        )}
      </Card>

      {/* Create/Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>
              {editingRule ? 'Edit Style Rule' : 'Add Style Rule'}
            </SheetTitle>
            <SheetDescription>
              {editingRule ? 'Modify this image consulting rule.' : 'Create a new recommendation rule for the Style Advisor.'}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Rule Type *</Label>
                <Select value={formData.rule_type} onValueChange={v => setFormData(p => ({ ...p, rule_type: v }))}>
                  <SelectTrigger className="mt-1 h-9" data-testid="form-rule-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(RULE_TYPE_LABELS).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Input Value *</Label>
                <Input value={formData.input_value} onChange={e => setFormData(p => ({ ...p, input_value: e.target.value }))}
                  placeholder="e.g., warm_deep, stocky, round" className="mt-1 h-9" data-testid="form-input-value" />
                {enums && formData.rule_type === 'colour_season' && (
                  <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Options: {enums.colour_seasons?.join(', ')}</p>
                )}
                {enums && formData.rule_type === 'body_shape' && (
                  <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Options: {enums.body_shapes?.join(', ')}</p>
                )}
                {enums && formData.rule_type === 'face_shape' && (
                  <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Options: {enums.face_shapes?.join(', ')}</p>
                )}
                {enums && formData.rule_type === 'occasion' && (
                  <p className="text-[9px] mt-0.5" style={{ color: 'var(--ink-muted)' }}>Options: {enums.occasions?.join(', ')}</p>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Recommendation Type *</Label>
                <Select value={formData.recommendation_type} onValueChange={v => setFormData(p => ({ ...p, recommendation_type: v }))}>
                  <SelectTrigger className="mt-1 h-9" data-testid="form-rec-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {enums?.recommendation_types?.map(t => (
                      <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Recommended Value *</Label>
                <Input value={formData.recommended_value} onChange={e => setFormData(p => ({ ...p, recommended_value: e.target.value }))}
                  placeholder="e.g., navy, notch, single_breasted" className="mt-1 h-9" data-testid="form-rec-value" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Strength</Label>
              <Select value={formData.strength} onValueChange={v => setFormData(p => ({ ...p, strength: v }))}>
                <SelectTrigger className="mt-1 h-9" data-testid="form-strength">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="strong">Strong (high priority)</SelectItem>
                  <SelectItem value="moderate">Moderate</SelectItem>
                  <SelectItem value="mild">Mild (low priority)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Explanation (client-friendly)</Label>
              <Textarea value={formData.explanation} onChange={e => setFormData(p => ({ ...p, explanation: e.target.value }))}
                placeholder="Why this recommendation works for this profile..."
                rows={3} className="mt-1" data-testid="form-explanation" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={formData.is_active} onCheckedChange={v => setFormData(p => ({ ...p, is_active: v }))} />
              <Label className="text-xs">Active</Label>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-1.5" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="save-rule-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editingRule ? 'Update' : 'Create'} Rule
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}

/* ============================================================
   Referral Tiers Section
   ============================================================ */
function ReferralTiersSection() {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [formData, setFormData] = useState({
    tier: 1,
    referral_count: 1,
    reward_description: '',
    reward_type: 'item',
    reward_value: 0,
    is_active: true,
  });

  const fetchTiers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await settingsAPI.getReferralTiers();
      setTiers(res.data || []);
    } catch (err) {
      toast.error('Failed to load referral tiers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTiers(); }, [fetchTiers]);

  const openEditSheet = (tier) => {
    setEditingTier(tier);
    setFormData({
      tier: tier.tier,
      referral_count: tier.referral_count,
      reward_description: tier.reward_description,
      reward_type: tier.reward_type,
      reward_value: tier.reward_value,
      is_active: tier.is_active,
    });
    setSheetOpen(true);
  };

  const openCreateSheet = () => {
    setEditingTier(null);
    const maxTier = Math.max(...tiers.map(t => t.tier), 0);
    setFormData({
      tier: maxTier + 1,
      referral_count: maxTier + 1,
      reward_description: '',
      reward_type: 'item',
      reward_value: 0,
      is_active: true,
    });
    setSheetOpen(true);
  };

  const handleSave = async () => {
    if (!formData.reward_description.trim()) {
      toast.error('Please enter a reward description');
      return;
    }
    setSaving(true);
    try {
      // Update the tier in local state
      let updatedTiers;
      if (editingTier) {
        updatedTiers = tiers.map(t => 
          t.tier === editingTier.tier ? { ...t, ...formData } : t
        );
      } else {
        updatedTiers = [...tiers, { ...formData, id: `temp-${Date.now()}` }];
      }
      
      // Sort by tier number
      updatedTiers.sort((a, b) => a.tier - b.tier);
      
      // Save all tiers
      await settingsAPI.updateReferralTiers({ tiers: updatedTiers });
      toast.success(editingTier ? 'Tier updated' : 'Tier created');
      setSheetOpen(false);
      fetchTiers();
    } catch (err) {
      toast.error('Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tierToDelete) => {
    if (!window.confirm(`Delete Tier ${tierToDelete.tier}?`)) return;
    try {
      const updatedTiers = tiers.filter(t => t.tier !== tierToDelete.tier);
      await settingsAPI.updateReferralTiers({ tiers: updatedTiers });
      toast.success('Tier deleted');
      fetchTiers();
    } catch (err) {
      toast.error('Failed to delete tier');
    }
  };

  const handleToggleActive = async (tier) => {
    try {
      const updatedTiers = tiers.map(t =>
        t.tier === tier.tier ? { ...t, is_active: !t.is_active } : t
      );
      await settingsAPI.updateReferralTiers({ tiers: updatedTiers });
      toast.success(`Tier ${tier.is_active ? 'deactivated' : 'activated'}`);
      fetchTiers();
    } catch (err) {
      toast.error('Failed to toggle tier');
    }
  };

  const formatRewardValue = (tier) => {
    if (tier.reward_type === 'discount_pct') return `${tier.reward_value}%`;
    if (tier.reward_value === 0) return 'Free';
    return `₹${tier.reward_value.toLocaleString('en-IN')}`;
  };

  if (loading) return <Skeleton className="h-64 w-full" />;

  return (
    <div className="space-y-4" data-testid="referral-tiers-section">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold" style={{ color: 'var(--brand-navy)' }}>Referral Reward Tiers</h3>
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
            Configure rewards for clients who refer new customers
          </p>
        </div>
        <Button onClick={openCreateSheet} className="gap-1.5 text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
          data-testid="add-tier-btn">
          <Plus className="w-4 h-4" /> Add Tier
        </Button>
      </div>

      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardContent className="p-0">
          {tiers.length === 0 ? (
            <p className="text-sm py-8 text-center" style={{ color: 'var(--ink-muted)' }}>
              No referral tiers configured
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-semibold w-16">Tier</TableHead>
                  <TableHead className="text-xs font-semibold w-24">Referrals</TableHead>
                  <TableHead className="text-xs font-semibold">Reward</TableHead>
                  <TableHead className="text-xs font-semibold w-24">Type</TableHead>
                  <TableHead className="text-xs font-semibold w-24">Value</TableHead>
                  <TableHead className="text-xs font-semibold w-20 text-center">Active</TableHead>
                  <TableHead className="text-xs font-semibold w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tiers.map(tier => (
                  <TableRow key={tier.id || tier.tier} data-testid={`tier-row-${tier.tier}`}>
                    <TableCell>
                      <Badge variant="outline" className="text-xs font-bold" style={{
                        background: 'rgba(197,165,90,0.12)',
                        color: 'var(--brand-navy)',
                        borderColor: 'rgba(197,165,90,0.30)',
                      }}>
                        {tier.tier}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm" style={{ color: 'var(--brand-navy)' }}>
                      {tier.referral_count} referral{tier.referral_count !== 1 ? 's' : ''}
                    </TableCell>
                    <TableCell>
                      <span className="text-sm font-medium" style={{ color: 'var(--brand-navy)' }}>
                        {tier.reward_description}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs capitalize" style={{
                        background: tier.reward_type === 'discount_pct' ? 'rgba(34,197,94,0.10)' : 'rgba(37,99,235,0.10)',
                        color: tier.reward_type === 'discount_pct' ? '#22C55E' : '#2563EB',
                        borderColor: 'transparent',
                      }}>
                        {tier.reward_type.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-medium" style={{ color: '#1E7A5F' }}>
                      {formatRewardValue(tier)}
                    </TableCell>
                    <TableCell className="text-center">
                      <Switch
                        checked={tier.is_active}
                        onCheckedChange={() => handleToggleActive(tier)}
                        data-testid={`tier-active-${tier.tier}`}
                      />
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="w-7 h-7 p-0">
                            <MoreHorizontal className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEditSheet(tier)}>
                            <Pencil className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDelete(tier)} className="text-red-600">
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit/Create Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-[420px] bg-white" data-testid="tier-sheet">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>
              {editingTier ? `Edit Tier ${editingTier.tier}` : 'Add Referral Tier'}
            </SheetTitle>
            <SheetDescription>Configure reward for this referral milestone</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Tier Number</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.tier}
                  onChange={e => setFormData(p => ({ ...p, tier: parseInt(e.target.value) || 1 }))}
                  className="mt-1"
                  data-testid="form-tier"
                />
              </div>
              <div>
                <Label className="text-xs">Required Referrals</Label>
                <Input
                  type="number"
                  min={1}
                  value={formData.referral_count}
                  onChange={e => setFormData(p => ({ ...p, referral_count: parseInt(e.target.value) || 1 }))}
                  className="mt-1"
                  data-testid="form-referral-count"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs">Reward Description</Label>
              <Input
                value={formData.reward_description}
                onChange={e => setFormData(p => ({ ...p, reward_description: e.target.value }))}
                placeholder="e.g., Complimentary Pocket Square"
                className="mt-1"
                data-testid="form-reward-description"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-xs">Reward Type</Label>
                <Select value={formData.reward_type} onValueChange={v => setFormData(p => ({ ...p, reward_type: v }))}>
                  <SelectTrigger className="mt-1" data-testid="form-reward-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="item">Item</SelectItem>
                    <SelectItem value="service">Service</SelectItem>
                    <SelectItem value="discount_pct">Discount %</SelectItem>
                    <SelectItem value="discount_flat">Discount ₹</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">
                  Value {formData.reward_type === 'discount_pct' ? '(%)' : '(₹)'}
                </Label>
                <Input
                  type="number"
                  min={0}
                  value={formData.reward_value}
                  onChange={e => setFormData(p => ({ ...p, reward_value: parseFloat(e.target.value) || 0 }))}
                  className="mt-1"
                  data-testid="form-reward-value"
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.is_active}
                onCheckedChange={v => setFormData(p => ({ ...p, is_active: v }))}
              />
              <Label className="text-xs">Active</Label>
            </div>
          </div>
          <SheetFooter>
            <Button variant="outline" onClick={() => setSheetOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} className="gap-1.5" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="save-tier-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {editingTier ? 'Update' : 'Create'} Tier
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}


/* ============================================================
   TAX / GST CONFIGURATION SECTION
============================================================ */
function TaxConfigSection() {
  const [config, setConfig] = useState({ gst_rate: 5, gst_on_fabrics: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await settingsAPI.get('tax_config');
        if (res.data?.value) setConfig(res.data.value);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await settingsAPI.update('tax_config', config);
      toast.success('Tax configuration saved');
    } catch { toast.error('Failed to save'); }
    setSaving(false);
  };

  if (loading) return <Skeleton className="h-40 w-full" />;

  return (
    <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="tax-config-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base" style={{ color: 'var(--brand-navy)' }}>Tax / GST Configuration</CardTitle>
        <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>Configure GST rates for purchase orders and costing.</p>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
          <div>
            <p className="text-sm font-medium" style={{ color: 'var(--navy-ink)' }}>Apply GST on Fabric Purchases</p>
            <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
              When enabled, GST is automatically calculated on all Purchase Orders
            </p>
          </div>
          <Switch checked={config.gst_on_fabrics} onCheckedChange={v => setConfig(p => ({ ...p, gst_on_fabrics: v }))}
            data-testid="gst-toggle" />
        </div>

        {config.gst_on_fabrics && (
          <div className="max-w-xs">
            <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>GST Rate (%)</Label>
            <Input type="number" step="0.5" min="0" max="28"
              value={config.gst_rate} onChange={e => setConfig(p => ({ ...p, gst_rate: parseFloat(e.target.value) || 0 }))}
              className="h-10 mt-1 rounded-lg" data-testid="gst-rate-input" />
            <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
              Standard rate for fabric purchases is 5%. Change only if tax rules update.
            </p>
          </div>
        )}

        <Button onClick={save} disabled={saving} className="h-9 rounded-lg text-xs gap-1.5"
          style={{ backgroundColor: 'var(--brand-navy)' }} data-testid="save-tax-config">
          {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {saving ? 'Saving...' : 'Save Tax Settings'}
        </Button>
      </CardContent>
    </Card>
  );
}

/* ============================================================
   TRY-ON AI CONFIGURATION SECTION
============================================================ */
function TryOnConfigSection() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [newApiKey, setNewApiKey] = useState('');

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await tryonAPI.getConfig();
      setConfig(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load try-on config');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const handleSave = async (updates) => {
    setSaving(true);
    try {
      await tryonAPI.updateConfig(updates);
      toast.success('Configuration saved');
      fetchConfig();
      setNewApiKey('');
    } catch (err) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tryon-config-section">
      {/* API Configuration */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Key className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            Google Gemini API Configuration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label className="text-xs">API Key</Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type={showApiKey ? 'text' : 'password'}
                value={newApiKey || config?.gemini_api_key_masked || ''}
                onChange={(e) => setNewApiKey(e.target.value)}
                placeholder="Enter Gemini API key..."
                className="font-mono text-xs"
                data-testid="gemini-api-key-input"
              />
              <Button variant="outline" size="sm" onClick={() => setShowApiKey(!showApiKey)}>
                <Eye className="w-4 h-4" />
              </Button>
              {newApiKey && (
                <Button size="sm" onClick={() => handleSave({ gemini_api_key: newApiKey })} disabled={saving}
                  style={{ backgroundColor: 'var(--brand-navy)' }}>
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                </Button>
              )}
            </div>
            <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
              Get your API key from <a href="https://aistudio.google.com/apikey" target="_blank" rel="noreferrer" className="underline">Google AI Studio</a>
            </p>
          </div>

          <Separator />

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Model</Label>
              <Select value={config?.model || 'gemini-2.5-flash-preview-05-20'} onValueChange={(v) => handleSave({ model: v })}>
                <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="gemini-2.5-flash-preview-05-20">gemini-2.5-flash (Fast, $0.04/image)</SelectItem>
                  <SelectItem value="gemini-2.0-flash-exp">gemini-2.0-flash-exp (Experimental)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Default Resolution</Label>
              <Select value={config?.default_resolution || '1K'} onValueChange={(v) => handleSave({ default_resolution: v })}>
                <SelectTrigger className="mt-1 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1K">1K (Fastest)</SelectItem>
                  <SelectItem value="2K">2K (Balanced)</SelectItem>
                  <SelectItem value="4K">4K (Best Quality)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Default Aspect Ratio</Label>
            <Select value={config?.default_aspect_ratio || '3:4'} onValueChange={(v) => handleSave({ default_aspect_ratio: v })}>
              <SelectTrigger className="mt-1 text-xs w-48"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3:4">3:4 (Portrait - Full Length)</SelectItem>
                <SelectItem value="4:3">4:3 (Landscape)</SelectItem>
                <SelectItem value="1:1">1:1 (Square)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Camera className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            Monthly Usage
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>
                {config?.monthly_usage?.images || 0}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Images Generated</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--surface-2)' }}>
              <p className="text-2xl font-bold" style={{ color: 'var(--brand-navy)' }}>
                {(config?.monthly_usage?.tokens || 0).toLocaleString()}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Tokens Consumed</p>
            </div>
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
              <p className="text-2xl font-bold text-green-600">
                ${(config?.monthly_usage?.estimated_cost || 0).toFixed(2)}
              </p>
              <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Estimated Cost</p>
            </div>
          </div>
          <p className="text-[9px] mt-3 text-center" style={{ color: 'var(--ink-muted)' }}>
            No budget cap — quality is the priority
          </p>
        </CardContent>
      </Card>

      {/* Generation Limits */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm" data-testid="tryon-generation-limits">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Filter className="w-4 h-4" style={{ color: 'var(--brand-gold)' }} />
            Generation Limits
          </CardTitle>
          <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>
            Control the maximum number of images and clients per campaign batch
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Max Looks per Client</Label>
              <Input type="number" min="1" max="10" className="mt-1 h-9"
                value={config?.max_looks_per_client || 3}
                onChange={e => {
                  const v = Math.max(1, Math.min(10, parseInt(e.target.value) || 1));
                  handleSave({ max_looks_per_client: v });
                }}
                data-testid="max-looks-input" />
              <p className="text-[9px] mt-1" style={{ color: 'var(--ink-muted)' }}>Default: 3. Max quality at 3 images per client.</p>
            </div>
            <div>
              <Label className="text-xs">Max Clients per Batch</Label>
              <Input type="number" min="1" max="50" className="mt-1 h-9"
                value={config?.max_clients_per_batch || 10}
                onChange={e => {
                  const v = Math.max(1, Math.min(50, parseInt(e.target.value) || 1));
                  handleSave({ max_clients_per_batch: v });
                }}
                data-testid="max-clients-input" />
              <p className="text-[9px] mt-1" style={{ color: 'var(--ink-muted)' }}>Default: 10. Create multiple campaigns for larger groups.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}


/* ============================================================
   Engagement Settings Section
   ============================================================ */
const ENGAGEMENT_TYPE_LABELS = {
  feedback_request: 'Feedback Request',
  dry_clean_reminder: 'Dry Clean Reminder',
  garment_care_tip: 'Garment Care Tip',
  reorder_nudge: 'Reorder Suggestion',
  birthday_greeting: 'Birthday Greeting',
  anniversary_greeting: 'Anniversary Greeting',
};

const ENGAGEMENT_TYPE_ICONS = {
  feedback_request: MessageSquare,
  dry_clean_reminder: Clock,
  garment_care_tip: Heart,
  reorder_nudge: ShoppingBag,
  birthday_greeting: Gift,
  anniversary_greeting: Heart,
};

const ENGAGEMENT_TYPE_COLORS = {
  feedback_request: '#2563EB',
  dry_clean_reminder: '#7C3AED',
  garment_care_tip: '#059669',
  reorder_nudge: '#D97706',
  birthday_greeting: '#EC4899',
  anniversary_greeting: '#EC4899',
};

function EngagementSettingsSection() {
  const [settings, setSettings] = useState(null);
  const [careRules, setCareRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [savingRule, setSavingRule] = useState(false);

  useEffect(() => {
    Promise.all([
      engagementAPI.getSettings(),
      engagementAPI.getCareRules(),
    ]).then(([settingsRes, rulesRes]) => {
      setSettings(settingsRes.data);
      setCareRules(rulesRes.data || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  const handleToggle = async (engagementType, enabled) => {
    const updates = [{ engagement_type: engagementType, enabled }];
    try {
      await engagementAPI.updateSettings(updates);
      setSettings(prev => ({
        ...prev,
        types: {
          ...prev.types,
          [engagementType]: { ...prev.types[engagementType], enabled },
        },
      }));
      toast.success(`${ENGAGEMENT_TYPE_LABELS[engagementType]} ${enabled ? 'enabled' : 'disabled'}`);
    } catch {
      toast.error('Failed to update');
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    const updates = Object.entries(settings?.types || {}).map(([key, val]) => ({
      engagement_type: key,
      enabled: val.enabled,
      days_after_delivery: val.days_after_delivery,
      custom_message_template: val.custom_message_template,
    }));
    try {
      await engagementAPI.updateSettings(updates);
      toast.success('Engagement settings saved');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleSaveCareRule = async () => {
    if (!editingRule) return;
    setSavingRule(true);
    try {
      await engagementAPI.updateCareRule(editingRule.id, {
        care_instruction: editingRule.care_instruction,
        reminder_interval_days: editingRule.reminder_interval_days,
        seasonal_tip: editingRule.seasonal_tip,
      });
      setCareRules(prev => prev.map(r => r.id === editingRule.id ? editingRule : r));
      setEditingRule(null);
      toast.success('Care rule updated');
    } catch {
      toast.error('Failed to save');
    }
    setSavingRule(false);
  };

  if (loading) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="space-y-6" data-testid="engagement-settings-section">
      {/* Engagement Types */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Heart className="w-4 h-4" style={{ color: '#EC4899' }} />
              Post-Delivery Engagement Types
            </CardTitle>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSaveSettings} disabled={saving} data-testid="save-engagement-settings">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save All
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
            Toggle engagement types on/off and customize timing. Engagements are automatically scheduled when an order is delivered.
          </p>
          
          {Object.entries(settings?.types || {}).map(([key, config]) => {
            const Icon = ENGAGEMENT_TYPE_ICONS[key] || MessageSquare;
            const color = ENGAGEMENT_TYPE_COLORS[key] || '#6B7280';
            return (
              <div key={key} className="flex items-start gap-4 p-3 rounded-lg border transition-colors"
                style={{ 
                  borderColor: config.enabled ? 'rgba(27,42,74,0.12)' : 'rgba(27,42,74,0.06)',
                  opacity: config.enabled ? 1 : 0.6,
                }}
                data-testid={`engagement-type-${key}`}>
                <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: color + '15' }}>
                  <Icon className="w-5 h-5" style={{ color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>
                      {config.label || ENGAGEMENT_TYPE_LABELS[key]}
                    </p>
                    <Badge variant="outline" className="text-[9px] px-1.5 py-0"
                      style={{ backgroundColor: config.enabled ? 'rgba(16,185,129,0.08)' : 'transparent', color: config.enabled ? '#059669' : 'var(--ink-muted)' }}>
                      {config.enabled ? 'Active' : 'Disabled'}
                    </Badge>
                  </div>
                  
                  {/* Days After Delivery */}
                  {config.days_after_delivery !== undefined && config.days_after_delivery !== 0 && (
                    <div className="flex items-center gap-2 mt-2">
                      <Label className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Days after delivery:</Label>
                      <Input type="number" value={config.days_after_delivery || 0} className="h-7 w-20 text-xs"
                        onChange={e => setSettings(prev => ({
                          ...prev,
                          types: {
                            ...prev.types,
                            [key]: { ...prev.types[key], days_after_delivery: parseInt(e.target.value) || 0 },
                          },
                        }))}
                        data-testid={`engagement-days-${key}`} />
                    </div>
                  )}
                  
                  {/* Custom Message Template */}
                  <div className="mt-2">
                    <Label className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>Message template:</Label>
                    <Textarea value={config.custom_message_template || ''} rows={2}
                      className="mt-1 text-xs" placeholder="Use {{client_name}}, {{garment_summary}}, etc."
                      onChange={e => setSettings(prev => ({
                        ...prev,
                        types: {
                          ...prev.types,
                          [key]: { ...prev.types[key], custom_message_template: e.target.value },
                        },
                      }))}
                      data-testid={`engagement-template-${key}`} />
                  </div>
                </div>
                <Switch checked={config.enabled} onCheckedChange={(checked) => handleToggle(key, checked)}
                  data-testid={`engagement-toggle-${key}`} />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Garment Care Rules */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
            <Shirt className="w-4 h-4" style={{ color: '#059669' }} />
            Garment Care Rules
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
            Care instructions and dry clean reminder intervals by garment and fabric type. These are sent as care tips and reminders after delivery.
          </p>
          
          <div className="space-y-2">
            {careRules.map(rule => (
              <div key={rule.id} className="p-3 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)' }}
                data-testid={`care-rule-${rule.id}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px] px-1.5 capitalize"
                      style={{ backgroundColor: 'rgba(27,42,74,0.05)' }}>
                      {rule.garment_type?.replace('_', ' ')}
                    </Badge>
                    <Badge variant="outline" className="text-[10px] px-1.5 capitalize"
                      style={{ backgroundColor: 'rgba(59,130,246,0.05)' }}>
                      {rule.fabric_type}
                    </Badge>
                    {rule.reminder_interval_days > 0 && (
                      <span className="text-[9px]" style={{ color: '#7C3AED' }}>
                        Dry clean every {rule.reminder_interval_days}d
                      </span>
                    )}
                  </div>
                  <Button variant="outline" size="sm" className="h-6 text-[10px] gap-1"
                    onClick={() => setEditingRule({ ...rule })} data-testid={`edit-care-rule-${rule.id}`}>
                    <Pencil className="w-3 h-3" /> Edit
                  </Button>
                </div>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--navy-ink)' }}>
                  {rule.care_instruction}
                </p>
                {rule.seasonal_tip && (
                  <p className="text-[10px] mt-1 italic" style={{ color: 'var(--ink-muted)' }}>
                    Seasonal tip: {rule.seasonal_tip}
                  </p>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Edit Care Rule Sheet */}
      <Sheet open={!!editingRule} onOpenChange={(open) => { if (!open) setEditingRule(null); }}>
        <SheetContent side="right" className="w-[500px] sm:w-[540px]">
          <SheetHeader>
            <SheetTitle style={{ color: 'var(--brand-navy)' }}>Edit Care Rule</SheetTitle>
            <SheetDescription>
              {editingRule?.garment_type?.replace('_', ' ')} / {editingRule?.fabric_type}
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Dry Clean Reminder Interval (days)</Label>
              <Input type="number" value={editingRule?.reminder_interval_days || 0} className="h-9 mt-1"
                onChange={e => setEditingRule(prev => ({ ...prev, reminder_interval_days: parseInt(e.target.value) || 0 }))}
                data-testid="care-rule-interval" />
              <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>Set to 0 to disable dry clean reminders</p>
            </div>
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Care Instruction</Label>
              <Textarea value={editingRule?.care_instruction || ''} rows={4} className="mt-1 text-xs"
                onChange={e => setEditingRule(prev => ({ ...prev, care_instruction: e.target.value }))}
                data-testid="care-rule-instruction" />
            </div>
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Seasonal Tip</Label>
              <Textarea value={editingRule?.seasonal_tip || ''} rows={2} className="mt-1 text-xs"
                onChange={e => setEditingRule(prev => ({ ...prev, seasonal_tip: e.target.value }))}
                data-testid="care-rule-seasonal" />
            </div>
          </div>
          <SheetFooter className="mt-6">
            <Button variant="outline" onClick={() => setEditingRule(null)}>Cancel</Button>
            <Button onClick={handleSaveCareRule} disabled={savingRule} className="gap-1" style={{ backgroundColor: 'var(--brand-navy)' }}
              data-testid="save-care-rule">
              {savingRule ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>
    </div>
  );
}


/* ============================================================
   Currency Settings Section - Exchange Rates
   ============================================================ */
function CurrencySettingsSection() {
  const [rates, setRates] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    internationalAPI.getExchangeRates()
      .then(res => { setRates(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await internationalAPI.updateExchangeRates({
        AED_to_INR: rates.AED_to_INR,
        USD_to_INR: rates.USD_to_INR,
        GBP_to_INR: rates.GBP_to_INR,
        EUR_to_INR: rates.EUR_to_INR,
      });
      toast.success('Exchange rates updated');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <Skeleton className="h-48 w-full" />;

  const currencies = [
    { key: 'AED_to_INR', label: 'AED → INR', flag: '🇦🇪', desc: 'UAE Dirham' },
    { key: 'USD_to_INR', label: 'USD → INR', flag: '🇺🇸', desc: 'US Dollar' },
    { key: 'GBP_to_INR', label: 'GBP → INR', flag: '🇬🇧', desc: 'British Pound' },
    { key: 'EUR_to_INR', label: 'EUR → INR', flag: '🇪🇺', desc: 'Euro' },
  ];

  return (
    <div className="space-y-6" data-testid="currency-settings-section">
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <IndianRupee className="w-4 h-4" style={{ color: '#059669' }} />
              Exchange Rates (to INR)
            </CardTitle>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-exchange-rates">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
            Set exchange rates for international orders. These rates are used for INR conversion in reporting and invoicing.
            Update periodically to reflect current market rates.
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            {currencies.map(({ key, label, flag, desc }) => (
              <div key={key} className="p-4 rounded-lg border" style={{ borderColor: 'rgba(27,42,74,0.08)' }}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-lg">{flag}</span>
                  <div>
                    <p className="text-sm font-semibold" style={{ color: 'var(--navy-ink)' }}>{label}</p>
                    <p className="text-[10px]" style={{ color: 'var(--ink-muted)' }}>{desc}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs" style={{ color: 'var(--ink-muted)' }}>1 {key.split('_')[0]} =</span>
                  <Input type="number" step="0.01" value={rates?.[key] || 0} className="h-9 w-28 text-sm"
                    onChange={e => setRates(prev => ({ ...prev, [key]: parseFloat(e.target.value) || 0 }))}
                    data-testid={`exchange-rate-${key}`} />
                  <span className="text-xs font-medium" style={{ color: 'var(--brand-navy)' }}>INR</span>
                </div>
              </div>
            ))}
          </div>
          
          {rates?.updated_at && (
            <p className="text-[10px] text-right" style={{ color: 'var(--ink-muted)' }}>
              Last updated: {new Date(rates.updated_at).toLocaleString('en-IN')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Currency Mapping Info */}
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold" style={{ color: 'var(--brand-navy)' }}>
            Auto-Currency Mapping
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs mb-3" style={{ color: 'var(--ink-muted)' }}>
            When creating an international order, the currency is automatically set based on the client's country:
          </p>
          <div className="grid grid-cols-4 gap-2 text-xs">
            {[
              { country: 'UAE', currency: 'AED' },
              { country: 'US / Canada', currency: 'USD' },
              { country: 'UK', currency: 'GBP' },
              { country: 'Others', currency: 'INR' },
            ].map(({ country, currency }) => (
              <div key={country} className="p-2 rounded-md text-center" style={{ backgroundColor: 'rgba(27,42,74,0.03)' }}>
                <p className="font-medium" style={{ color: 'var(--navy-ink)' }}>{country}</p>
                <p style={{ color: 'var(--ink-muted)' }}>{currency}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

/* ============================================================
   Consultation Settings Section - Video Links & Remote Flow
   ============================================================ */
function ConsultationSettingsSection() {
  const [settings, setSettings] = useState({ video_link: '', video_platform: 'google_meet', measurement_guide_url: '' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    internationalAPI.getConsultationSettings()
      .then(res => { setSettings(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await internationalAPI.updateConsultationSettings(settings);
      toast.success('Consultation settings updated');
    } catch {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  if (loading) return <Skeleton className="h-48 w-full" />;

  return (
    <div className="space-y-6" data-testid="consultation-settings-section">
      <Card className="rounded-xl border border-border/80 bg-white shadow-sm">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--brand-navy)' }}>
              <Camera className="w-4 h-4" style={{ color: '#2563EB' }} />
              Remote Consultation Settings
            </CardTitle>
            <Button size="sm" className="gap-1 rounded-lg text-xs" style={{ backgroundColor: 'var(--brand-navy)' }}
              onClick={handleSave} disabled={saving} data-testid="save-consultation-settings">
              {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} Save
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-xs" style={{ color: 'var(--ink-muted)' }}>
            Configure video consultation links for remote/international clients. The salesperson shares their screen while using the visual configurator.
          </p>
          
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Video Platform</Label>
              <Select value={settings.video_platform} onValueChange={v => setSettings(prev => ({ ...prev, video_platform: v }))}>
                <SelectTrigger className="h-9 mt-1" data-testid="video-platform-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="google_meet">Google Meet</SelectItem>
                  <SelectItem value="zoom">Zoom</SelectItem>
                  <SelectItem value="teams">Microsoft Teams</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Video Consultation Link</Label>
              <Input value={settings.video_link} className="h-9 mt-1" placeholder="https://meet.google.com/xxx-xxxx-xxx"
                onChange={e => setSettings(prev => ({ ...prev, video_link: e.target.value }))}
                data-testid="video-link-input" />
              <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                A "Copy Consultation Link" button will be shown during remote orders.
              </p>
            </div>
            
            <div>
              <Label className="text-xs font-medium" style={{ color: 'var(--ink-muted)' }}>Self-Measurement Guide URL</Label>
              <Input value={settings.measurement_guide_url} className="h-9 mt-1" placeholder="https://example.com/measurement-guide.pdf"
                onChange={e => setSettings(prev => ({ ...prev, measurement_guide_url: e.target.value }))}
                data-testid="measurement-guide-input" />
              <p className="text-[10px] mt-1" style={{ color: 'var(--ink-muted)' }}>
                PDF or webpage with illustrated self-measurement instructions for remote clients.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
